// Configuracion default y tipo del Reglamento Interno generado dinamicamente.
// Port de los datos variables del HTML "_TEMPLATE Reglamento Habitia.html".

/**
 * @typedef {{ id: string, etapa: string, sancion: string, detalle: string }} Sancion
 * @typedef {{ id: string, lbl: string, val: string, note?: string }} HoraItem
 * @typedef {{ id: string, texto: string }} Prohibicion
 * @typedef {Object} CapitulosVisibles
 * @property {boolean} derechos           Cap I
 * @property {boolean} obligaciones       Cap II
 * @property {boolean} prohibiciones      Cap III
 * @property {boolean} personal           Cap IV
 * @property {boolean} comision           Cap V
 * @property {boolean} administrador      Cap VI
 * @property {boolean} personalContratado Cap VII
 * @property {boolean} hallAccesos        Cap VIII
 * @property {boolean} mascotas           Cap IX
 * @property {boolean} ascensores         Cap X
 * @property {boolean} garajes            Cap XI
 * @property {boolean} gimnasio           Cap XII
 * @property {boolean} jardines           Cap XIII
 * @property {boolean} barbacoa           Cap XIV
 * @property {boolean} piscina            Cap XV
 * @property {boolean} pagos              Cap XVI
 * @property {boolean} vacios             Cap XVII
 * @property {boolean} sanciones          Cap XVIII
 * @property {boolean} vigencia           Cap XIX
 * @typedef {Object} ReglamentoConfig
 * @property {string} edificio
 * @property {string} padron
 * @property {string} vigenciaDesde
 * @property {string} version
 * @property {string} fechaAprobacion
 * @property {string} administracion
 * @property {string} contacto
 * @property {CapitulosVisibles} capitulosVisibles
 * @property {Prohibicion[]} prohibiciones
 * @property {Sancion[]} sanciones
 * @property {{ gimnasio: HoraItem[], barbacoa: HoraItem[], piscina: HoraItem[] }} horarios
 */

// Lista de los 19 capitulos toggleables, en el orden en que aparecen.
// Cada uno se puede activar/desactivar individualmente desde el form.
// `pagina` es la key de la pagina del template donde vive (para agrupar en el form).
export const CAPITULOS_TOGGLEABLES = [
  { key: "derechos",           label: "Derechos de los habitantes",     capRomano: "I",     pagina: "habitantes" },
  { key: "obligaciones",       label: "Obligaciones de los habitantes", capRomano: "II",    pagina: "habitantes" },
  { key: "prohibiciones",      label: "Prohibiciones",                   capRomano: "III",   pagina: "prohibiciones" },
  { key: "personal",           label: "Personal del edificio",           capRomano: "IV",    pagina: "personal-comision" },
  { key: "comision",           label: "Comisión del edificio",           capRomano: "V",     pagina: "personal-comision" },
  { key: "administrador",      label: "Administrador",                   capRomano: "VI",    pagina: "administrador" },
  { key: "personalContratado", label: "Personal de servicio contratado", capRomano: "VII",   pagina: "accesos-mascotas" },
  { key: "hallAccesos",        label: "Hall de entrada y accesos",       capRomano: "VIII",  pagina: "accesos-mascotas" },
  { key: "mascotas",           label: "Animales domésticos",             capRomano: "IX",    pagina: "accesos-mascotas" },
  { key: "ascensores",         label: "Ascensores",                       capRomano: "X",     pagina: "ascensores-garajes" },
  { key: "garajes",            label: "Garajes",                          capRomano: "XI",    pagina: "ascensores-garajes" },
  { key: "gimnasio",           label: "Gimnasio",                         capRomano: "XII",   pagina: "gimnasio-jardines" },
  { key: "jardines",           label: "Jardines y exteriores",           capRomano: "XIII",  pagina: "gimnasio-jardines" },
  { key: "barbacoa",           label: "Barbacoa",                         capRomano: "XIV",   pagina: "barbacoa" },
  { key: "piscina",            label: "Piscina, hidromasaje y sauna",    capRomano: "XV",    pagina: "piscina" },
  { key: "pagos",              label: "Pago de gastos comunes",          capRomano: "XVI",   pagina: "pagos-vacios" },
  { key: "vacios",             label: "Apartamentos desocupados",         capRomano: "XVII",  pagina: "pagos-vacios" },
  { key: "sanciones",          label: "Sanciones",                        capRomano: "XVIII", pagina: "sanciones" },
  { key: "vigencia",           label: "Vigencia y disposiciones",        capRomano: "XIX",   pagina: "sanciones" },
];

// Mapa pagina -> capitulos que contiene (para `buildPagesList`).
export const CAPITULOS_POR_PAGINA = CAPITULOS_TOGGLEABLES.reduce((acc, cap) => {
  if (!acc[cap.pagina]) acc[cap.pagina] = [];
  acc[cap.pagina].push(cap.key);
  return acc;
}, {});

// Helper: una pagina se renderiza solo si al menos uno de sus capitulos esta visible.
export function paginaActiva(paginaKey, capitulosVisibles) {
  const caps = CAPITULOS_POR_PAGINA[paginaKey] || [];
  return caps.some((k) => !!capitulosVisibles[k]);
}

// Numeros romanos para los 19 capitulos (max 19 visibles).
const ROMANOS = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV","XV","XVI","XVII","XVIII","XIX"];

// Construye el mapa { keyDelCap: nuevoRomanoSecuencial } para los capitulos visibles.
// Si el usuario apaga el Cap I (derechos), entonces lo que era Cap II (obligaciones) pasa a ser Cap I.
// Las cross-references a otros capitulos tambien usan este mapa para mantener consistencia.
export function buildCapRomanoMap(capitulosVisibles) {
  const map = {};
  let i = 0;
  for (const cap of CAPITULOS_TOGGLEABLES) {
    if (capitulosVisibles[cap.key]) {
      map[cap.key] = ROMANOS[i++] || `#${i}`;
    }
  }
  return map;
}

export function defaultProhibiciones() {
  return [
    { id: "p1",  texto: "Dar a los bienes comunes destino distinto al específico." },
    { id: "p2",  texto: "Depositar materiales o sustancias peligrosas o inflamables." },
    { id: "p3",  texto: "Colocar letreros, banderas o toldos sin autorización." },
    { id: "p4",  texto: "Toldos o cortinas exteriores sin la especificación uniforme." },
    { id: "p5",  texto: "Pintar fachada, vidrios o bienes comunes sin autorización." },
    { id: "p6",  texto: "Aparatos de TV, audio o similares a alto volumen." },
    { id: "p7",  texto: "Arrojar botellas, colillas u objetos por ventanas o áreas comunes." },
    { id: "p8",  texto: "Arrojar residuos que afecten u obstruyan las cañerías." },
    { id: "p9",  texto: "Antenas o aire acondicionado fuera de los lugares habilitados." },
    { id: "p10", texto: "Tender ropa en ventanas o lugares visibles desde el exterior." },
    { id: "p11", texto: "Transitar por sectores comunes descalzos o mojados." },
    { id: "p12", texto: "Reparaciones ruidosas fuera de 09:00–20:00 lun–sáb." },
    { id: "p13", texto: "Extractores de cocina sin interceptores de grasa." },
    { id: "p14", texto: "Fumar en áreas comunes cerradas del edificio." },
    { id: "p15", texto: "Cocinar, asar o usar barbacoas en los balcones." },
  ];
}

export function defaultSanciones() {
  return [
    { id: "s1", etapa: "1ª",  sancion: "Apercibimiento", detalle: "Notificación formal por escrito al infractor." },
    { id: "s2", etapa: "2ª",  sancion: "Multa de 2 UR", detalle: "2 Unidades Reajustables. Ejecutable como gasto común ordinario." },
    { id: "s3", etapa: "3ª+", sancion: "Multa acumulable", detalle: "Reincidencia: acumulable 2, 4, 6 UR y así sucesivamente." },
  ];
}

export function defaultHorarios() {
  return {
    gimnasio: [
      { id: "h-g1", lbl: "Horario",     val: "06 — 24 hs", note: "Todos los días" },
      { id: "h-g2", lbl: "Edad mínima", val: "12 años" },
      { id: "h-g3", lbl: "Uso",         val: "Residentes", note: "Sin clases ni lucro" },
    ],
    barbacoa: [
      { id: "h-b1", lbl: "Horario",         val: "10–17 / 19–24",  note: "Vier/Sáb hasta 03 hs" },
      { id: "h-b2", lbl: "Capacidad máx.",  val: "30 personas",     note: "Disposición Bomberos" },
      { id: "h-b3", lbl: "Reserva máx.",    val: "90 días",         note: "1 reserva anual a 1 año" },
    ],
    piscina: [
      { id: "h-p1", lbl: "Horario piscina", val: "06 — 24 hs" },
      { id: "h-p2", lbl: "Menores 12",       val: "Con adulto" },
      { id: "h-p3", lbl: "Invitados",        val: "Máx. 3", note: "Por apartamento" },
    ],
  };
}

/** @returns {ReglamentoConfig} */
export function defaultConfig() {
  return {
    edificio: "Millán 4157",
    padron: "N° 51.445",
    vigenciaDesde: "Desde 05/05/2026",
    version: "v1.0 · Asamblea",
    fechaAprobacion: "9 noviembre 2018",
    administracion: "Habitia",
    contacto: "info@habitia.ai",
    capitulosVisibles: {
      derechos: true,
      obligaciones: true,
      prohibiciones: true,
      personal: true,
      comision: true,
      administrador: true,
      personalContratado: true,
      hallAccesos: true,
      mascotas: true,
      ascensores: true,
      garajes: true,
      gimnasio: true,
      jardines: true,
      barbacoa: true,
      piscina: true,
      pagos: true,
      vacios: true,
      sanciones: true,
      vigencia: true,
    },
    prohibiciones: defaultProhibiciones(),
    sanciones: defaultSanciones(),
    horarios: defaultHorarios(),
  };
}
